export function toCents(value) {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

export function getGlobalDiscounts(document) {
    const resumen = document?.resumen || {};
    const typedDiscounts = [
        { type: 'gravada', amount: toCents(resumen.descuGravada) },
        { type: 'exenta', amount: toCents(resumen.descuExenta) },
        { type: 'noSuj', amount: toCents(resumen.descuNoSuj) }
    ].filter(discount => discount.amount > 0);
    const totalDiscount = toCents(resumen.totalDescu);
    const typedTotal = typedDiscounts.reduce((sum, discount) => sum + discount.amount, 0);
    if (typedDiscounts.length) {
        if (totalDiscount > typedTotal) typedDiscounts.push({ type: 'all', amount: totalDiscount - typedTotal });
        return typedDiscounts;
    }
    return totalDiscount > 0 ? [{ type: 'all', amount: totalDiscount }] : [];
}

export function getDiscountLines(document) {
    const source = Array.isArray(document?.cuerpoDocumento) ? document.cuerpoDocumento : [];
    return source.map((line, index) => {
        const gravada = toCents(line?.ventaGravada);
        const exenta = toCents(line?.ventaExenta);
        const noSuj = toCents(line?.ventaNoSuj);
        const type = gravada > 0 ? 'gravada' : (exenta > 0 ? 'exenta' : 'noSuj');
        const original = gravada || exenta || noSuj;
        return { index, selected: true, description: line?.descripcion || `Producto ${index + 1}`, quantity: line?.cantidad || 1, original, type, discount: 0, result: original };
    }).filter(line => line.original > 0);
}

export function calculateDiscountPreview(document, lines) {
    const discounts = getGlobalDiscounts(document);
    const total = discounts.reduce((sum, discount) => sum + discount.amount, 0);
    const selected = lines.filter(line => line.selected);
    const capacityByType = selected.reduce((result, line) => {
        result[line.type] = (result[line.type] || 0) + line.original;
        result.all = (result.all || 0) + line.original;
        return result;
    }, {});
    lines.forEach(line => { line.discount = 0; line.result = line.original; });
    discounts.forEach(globalDiscount => {
        const eligible = selected.filter(line => globalDiscount.type === 'all' || line.type === globalDiscount.type);
        const capacity = globalDiscount.type === 'all' ? capacityByType.all || 0 : capacityByType[globalDiscount.type] || 0;
        if (!eligible.length || capacity < globalDiscount.amount) return;
        let remaining = globalDiscount.amount;
        eligible.forEach((line, index) => {
            const discount = index === eligible.length - 1 ? remaining : Math.min(line.original, Math.floor(globalDiscount.amount * line.original / capacity));
            line.discount += discount;
            remaining -= discount;
        });
    });
    const distributed = lines.reduce((sum, line) => {
        line.result = line.original - line.discount;
        return sum + line.discount;
    }, 0);
    const capacity = selected.reduce((sum, line) => sum + line.original, 0);
    const error = !selected.length || capacity < total
        ? 'Los productos seleccionados no tienen capacidad suficiente para aplicar el descuento completo.'
        : distributed !== total
            ? 'Seleccione productos compatibles con el tipo de descuento para distribuirlo completamente.'
            : '';
    return { distributed: distributed / 100, pending: Math.max(0, total - distributed) / 100, error, canConfirm: !error && distributed === total };
}

export function getProcessingIva(document, details) {
    const includesIva = !['CCF', 'NC', 'ND'].includes(document?.invoiceType);
    const totalGravada = (details || []).reduce(
        (sum, line) => sum + toCents(line?.ventaGravada),
        0
    ) / 100;
    if (totalGravada <= 0) return 0;

    if (includesIva) {
        const neto = parseFloat((totalGravada / 1.13).toFixed(2));
        return parseFloat((totalGravada - neto).toFixed(2));
    }
    return parseFloat((totalGravada * 0.13).toFixed(2));
}

export function getAdjustedTotals(document, details) {
    const totals = (details || []).reduce((result, line) => {
        result.gravada += toCents(line?.ventaGravada);
        result.exenta += toCents(line?.ventaExenta);
        result.noSuj += toCents(line?.ventaNoSuj);
        return result;
    }, { gravada: 0, exenta: 0, noSuj: 0 });
    const totalGravada = totals.gravada / 100;
    const totalExenta = totals.exenta / 100;
    const totalNoSuj = totals.noSuj / 100;
    const iva = getProcessingIva(document, details);
    const subtotal = parseFloat((totalGravada + totalExenta + totalNoSuj).toFixed(2));
    const totalOperacion = ['CCF', 'NC', 'ND'].includes(document?.invoiceType)
        ? parseFloat((subtotal + iva).toFixed(2))
        : subtotal;
    return {
        totalGravada: totalGravada,
        totalExenta: totalExenta,
        totalNoSuj: totalNoSuj,
        totalIva: iva,
        subTotal: subtotal,
        montoTotalOperacion: totalOperacion,
        totalPagar: totalOperacion
    };
}

export function getDiscountedDetails(document, lines) {
    const source = Array.isArray(document?.cuerpoDocumento)
        ? document.cuerpoDocumento
        : [];
    const adjusted = source.map(line => ({ ...line }));

    adjusted.forEach(target => {
        ['ventaGravada', 'ventaExenta', 'ventaNoSuj'].forEach(field => {
            if (target[field] !== undefined && target[field] !== null) {
                target[field] = toCents(target[field]) / 100;
            }
        });
    });

    (lines || []).forEach(line => {
        const target = adjusted[line.index];
        const discount = Number(line.discount || 0) / 100;
        if (!target || discount <= 0) return;

        const field = line.type === 'gravada'
            ? 'ventaGravada'
            : (line.type === 'exenta' ? 'ventaExenta' : 'ventaNoSuj');
        const current = Number(target[field] || 0);
        target[field] = parseFloat(Math.max(0, current - discount).toFixed(2));
        target.montoDescu = parseFloat(
            (Number(target.montoDescu || 0) + discount).toFixed(2)
        );
    });


    const summaryTotal = ['totalGravada', 'totalExenta', 'totalNoSuj']
        .reduce((sum, field) => sum + toCents(document?.resumen?.[field]), 0);
    const discountTotal = getGlobalDiscounts(document)
        .reduce((sum, discount) => sum + discount.amount, 0);
    const adjustedTotal = adjusted.reduce((sum, line) => (
        sum
        + toCents(line?.ventaGravada)
        + toCents(line?.ventaExenta)
        + toCents(line?.ventaNoSuj)
    ), 0);
    const correction = summaryTotal - discountTotal - adjustedTotal;
    if (correction !== 0) {
        const targetLine = [...(lines || [])]
            .reverse()
            .find(line => line.selected && adjusted[line.index]);
        if (targetLine) {
            const target = adjusted[targetLine.index];
            const field = targetLine.type === 'gravada'
                ? 'ventaGravada'
                : (targetLine.type === 'exenta' ? 'ventaExenta' : 'ventaNoSuj');
            const current = toCents(target[field]);
            target[field] = Math.max(0, current + correction) / 100;
        }
    }

    return adjusted;
}
